// Player profile rules, shared by the game (src/core/profile.js) and the functions — pure functions.
// A profile signs a Hall of Fame entry: an avatar from a closed list, a nickname, and the place found by geolocation
// (country code and city, both optional). The emoji + nickname pair is unique among the Hall of Fame players (nameKey).

export const PSEUDO_MIN_LENGTH = 3;
export const PSEUDO_MAX_LENGTH = 15;
export const CITY_MAX_LENGTH = 30;

// The avatars of ZERO SLANG (zero-slang/src/data/avatars.js), without their outline colours
export const AVATARS = Object.freeze([
  { id: 'fox', emoji: '🦊' },
  { id: 'frog', emoji: '🐸' },
  { id: 'unicorn', emoji: '🦄' },
  { id: 'alien', emoji: '👽' },
  { id: 'ghost', emoji: '👻' },
  { id: 'panda', emoji: '🐼' },
  { id: 'robot', emoji: '🤖' },
  { id: 'octopus', emoji: '🐙' },
  { id: 'cat', emoji: '🐱' },
  { id: 'lion', emoji: '🦁' },
  { id: 'dino', emoji: '🦖' },
  { id: 'cool', emoji: '😎' },
]);

// Anything that isn't a letter (accents included), a digit, "_" or "-"
const PSEUDO_FORBIDDEN = /[^\p{L}\p{N}_-]/gu;
// City: letters, digits, space, apostrophe, hyphen, dot
const CITY_FORBIDDEN = /[^\p{L}\p{N} '\-.]/gu;

/** Strips forbidden characters and truncates. To apply on every keystroke. */
export function normalizePseudo(raw) {
  return String(raw ?? '').normalize('NFC').replace(PSEUDO_FORBIDDEN, '').slice(0, PSEUDO_MAX_LENGTH);
}

/**
 * Language-independent validation: returns an error code, never a text.
 * @returns {{ valid: boolean, errorCode: 'invalid-chars'|'too-short'|'too-long'|null }}
 */
export function validatePseudo(pseudo) {
  const p = String(pseudo ?? '').normalize('NFC');
  // Forbidden characters are detected before truncating: a nickname that is too long but made of valid characters only
  // is reported as "too-long", not "invalid-chars"
  if (p.replace(PSEUDO_FORBIDDEN, '') !== p) return { valid: false, errorCode: 'invalid-chars' };
  if (p.length < PSEUDO_MIN_LENGTH) return { valid: false, errorCode: 'too-short' };
  if (p.length > PSEUDO_MAX_LENGTH) return { valid: false, errorCode: 'too-long' };
  return { valid: true, errorCode: null };
}

/** City: safe characters, single spaces, truncated. */
export function normalizeCity(raw) {
  return String(raw ?? '')
    .normalize('NFC')
    .replace(CITY_FORBIDDEN, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, CITY_MAX_LENGTH);
}

/** The avatar of an id, or null if it isn't in the list. */
export const avatarById = (id) => AVATARS.find((a) => a.id === id) ?? null;

// Two-letter region codes that name no country
const NOT_COUNTRIES = new Set(['EU', 'EZ', 'UN', 'QO', 'XA', 'XB', 'ZZ']);

/** An existing ISO 3166-1 alpha-2 country code, upper case ("fr" → "FR"), or null. */
export function normalizeCountryCode(code) {
  if (typeof code !== 'string' || !/^[A-Za-z]{2}$/.test(code)) return null;
  const upper = code.toUpperCase();
  if (NOT_COUNTRIES.has(upper)) return null;
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' }).of(upper);
    return name && name !== upper && name !== 'Unknown Region' ? upper : null;
  } catch {
    return null;
  }
}

/** Key of the emoji + nickname pair, unique among the Hall of Fame players (case-insensitive). */
export const nameKey = (avatarId, pseudo) => `${avatarId}_${String(pseudo).normalize('NFC').toLowerCase()}`;

/**
 * The profile signing a Hall of Fame entry, checked: { pseudo, avatar, emoji, city?, countryCode? }, or an error code.
 * @param {unknown} raw { pseudo, avatar, city?, countryCode? }
 * @returns {{ profile: object|null, errorCode: string|null }}
 */
export function cleanSignature(raw) {
  if (!raw || typeof raw !== 'object') return { profile: null, errorCode: 'invalid-profile' };
  const { valid, errorCode } = validatePseudo(raw.pseudo);
  if (!valid) return { profile: null, errorCode: `pseudo-${errorCode}` };
  const avatar = avatarById(raw.avatar);
  if (!avatar) return { profile: null, errorCode: 'invalid-avatar' };
  const profile = { pseudo: String(raw.pseudo).normalize('NFC'), avatar: avatar.id, emoji: avatar.emoji };
  const city = normalizeCity(raw.city);
  if (city) profile.city = city;
  if (raw.countryCode != null && raw.countryCode !== '') {
    const countryCode = normalizeCountryCode(raw.countryCode);
    if (!countryCode) return { profile: null, errorCode: 'invalid-country' };
    profile.countryCode = countryCode;
  }
  return { profile, errorCode: null };
}
